import { TileData } from '../types';
import { getActivePorts, OPPOSITE_PORT, PORT_DELTA } from '../constants/tiles';
import type { Port, Rotation } from '../types';

export interface ConnectionResult {
  liveMap: boolean[][];
  distanceMap: number[][];
  allLive: boolean; // true = end tile (bottom-right) is reachable from source
}

export function checkConnections(grid: TileData[][]): ConnectionResult {
  const rows = grid.length;
  const cols = grid[0].length;

  const liveMap: boolean[][] = Array.from({ length: rows }, () => new Array(cols).fill(false));
  const distanceMap: number[][] = Array.from({ length: rows }, () => new Array(cols).fill(-1));

  // BFS from source (0,0)
  const queue: Array<[number, number]> = [[0, 0]];
  liveMap[0][0] = true;
  distanceMap[0][0] = 0;

  while (queue.length > 0) {
    const [r, c] = queue.shift()!;
    const tile = grid[r][c];

    // Empty tiles have no ports — BFS stops here
    if (!tile.isOnPath) continue;

    const ports = getActivePorts(tile.type, tile.logicalRotation as Rotation);

    for (const port of ports) {
      const [dr, dc] = PORT_DELTA[port];
      const nr = r + dr;
      const nc = c + dc;
      if (nr < 0 || nr >= rows || nc < 0 || nc >= cols) continue;
      if (liveMap[nr][nc]) continue;

      const neighbor = grid[nr][nc];
      if (!neighbor.isOnPath) continue; // never propagate into empty tiles

      const neighborPorts = getActivePorts(neighbor.type, neighbor.logicalRotation as Rotation);
      const required = OPPOSITE_PORT[port];

      if (neighborPorts.includes(required)) {
        liveMap[nr][nc] = true;
        distanceMap[nr][nc] = distanceMap[r][c] + 1;
        queue.push([nr, nc]);
      }
    }
  }

  // Win = end tile (bottom-right corner) is live
  const allLive = liveMap[rows - 1][cols - 1];

  return { liveMap, distanceMap, allLive };
}
